import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import injectSheet from 'react-jss'
import PageHead from '../../page-head/page-head'
import Button from 'pobble-components/dist/button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus'
import { faCaretCircleLeft } from '@fortawesome/pro-solid-svg-icons/faCaretCircleLeft'
import { faCaretCircleRight } from '@fortawesome/pro-solid-svg-icons/faCaretCircleRight'
import { faPaperPlane } from '@fortawesome/pro-solid-svg-icons/faPaperPlane'
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck'
import { faFilter } from '@fortawesome/pro-solid-svg-icons/faFilter'
import { faFileAlt } from '@fortawesome/pro-regular-svg-icons/faFileAlt'
import { faFile } from '@fortawesome/pro-regular-svg-icons/faFile'
import { faClipboardCheck } from '@fortawesome/pro-regular-svg-icons/faClipboardCheck'
import { faClipboard } from '@fortawesome/pro-regular-svg-icons/faClipboard'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchFeedItems,
  rejectWork,
  approveWork,
  addStudentsToSession
} from '../../../stores/feed-items/feed-items.actions'
import {
  getFeedItemsList,
  getFeedItemsState
} from '../../../stores/feed-items/feed-items.selectors'
import classNames from 'classnames'
import {
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter
} from 'pobble-components/dist/modal'
import { errorHandler } from 'pobble-components/dist/error-handler'
import FormLabel from 'pobble-components/dist/form-elements/form-label'
import FormSelect from 'pobble-components/dist/form-elements/form-select'
import FormCheckbox from 'pobble-components/dist/form-elements/form-checkbox'
import FormTextArea from 'pobble-components/dist/form-elements/form-text-area'
import { toast } from 'pobble-components/dist/toast'
import { PobbleAvatar } from 'pobble-components/dist/pobble-avatar'
import duckRocket from './duck-rocket.gif'
import Spinner from 'pobble-components/dist/spinner'
import { apiGetMe } from '../../../common/api/profile'
import { format } from 'date-fns'
import Bone from 'pobble-components/dist/bone'
import {
  apiGetStudentsPerSession,
  apiGetPresentationAction
} from '../../../common/api/pupil-activity'
import mountains from './mountains.png'
import PupilSelectorUpload from '../../pupil-selector/pupil-selector'
import { useHistory, useRouteMatch } from 'react-router-dom'
import PobbleLink from 'pobble-components/dist/pobble-link'
import { usePrevious } from 'pobble-components/dist/utils'
import { URLS } from 'pobble-components/dist/setup'
import ButtonElement from 'pobble-components/dist/button-element'
import PupilSelector from 'pobble-components/dist/pupil-selector'
import WorkForm from '../../work-form/work-form'
import { MediaLoader } from 'pobble-components/dist/work'
import { WorkHighlighter } from 'pobble-components/dist/work/highlighter'
import {
  useWorkReviews,
  WorkReviews
} from 'pobble-components/dist/work/reviews'
import { fullVHExceptHeader } from 'pobble-components/dist/jss-mixins'
import { openConfirmationDialog } from 'pobble-components/dist/modal/confirmation-dialog'
import { useWorkUpload } from '../../../hooks/work'

export const directUploadStyles = {
  content: {
    padding: '0',
    '@media (min-width: 575px)': {
      minWidth: '700px'
    }
  }
}

const DirectUploadModal = injectSheet(directUploadStyles)(
  ({ classes, onUpload, ...props }) => {
    return (
      <Modal closeButton {...props}>
        <ModalHeader>Upload work</ModalHeader>
        <ModalContent legacy className={classes.content}>
          <PupilSelectorUpload onUpload={onUpload} />
        </ModalContent>
      </Modal>
    )
  }
)

const studentItemStyles = ({ COLORS }) => ({
  student: {
    width: '100%',
    background: 'none',
    border: 'none',
    borderRadius: '0',
    padding: '12px 20px',
    boxShadow: 'none',
    borderBottom: '1px solid #ececec',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    color: COLORS.TEXT.LIGHT_GREY.NORMAL,
    '@media (min-width: 813px)': {
      cursor: 'pointer',
      '&:hover': {
        color: COLORS.BLUE.NORMAL
      }
    }
  },
  active: {
    position: 'relative',
    '&:before': {
      position: 'absolute',
      left: '0',
      top: '0',
      background: COLORS.BLUE.NORMAL,
      content: '""',
      height: '100%',
      width: '10px',
      marginRight: '10px'
    },
    '@media (min-width: 813px)': {
      cursor: 'default',
      '&:hover': {
        color: COLORS.TEXT.LIGHT_GREY.NORMAL
      }
    }
  },
  stats: {
    marginLeft: 'auto',
    color: '#999',
    display: 'flex',
    fontSize: '18px',
    '& > *': {
      marginLeft: '.6em'
    }
  },
  disabledStat: {
    color: '#ddd'
  },
  evidenceBank: {
    color: COLORS.PURPLE.NORMAL
  },
  published: {
    color: COLORS.GREEN.NORMAL
  },
  worksCount: {
    position: 'relative',
    '& > span': {
      position: 'absolute',
      top: '100%',
      width: '100%',
      left: '0',
      fontSize: '.5em',
      textAlign: 'center',
      marginTop: '.1em'
    }
  },
  studentAvatar: {
    fontSize: '32px',
    marginRight: '10px',
    flexShrink: '0'
  }
})

const StudentItem = injectSheet(studentItemStyles)(
  ({ as = 'button', active, classes, student, ...props }) => {
    const Component = as
    return (
      <Component
        key={student.id}
        className={classNames(classes.student, { [classes.active]: active })}
        {...props}>
        <PobbleAvatar
          className={classes.studentAvatar}
          avatar={student.avatar}
        />
        {student.full_name}
        <div className={classes.stats}>
          <div className={classes.worksCount} title="Works">
            <FontAwesomeIcon
              className={classNames({
                [classes.disabledStat]: !student.work_versions_count
              })}
              icon={faFileAlt}
            />
            {!!student.work_versions_count && (
              <span>{student.work_versions_count}</span>
            )}
          </div>
          <div
            title="Published"
            className={classNames(classes.workState, {
              [classes.disabledStat]: !student.work_state,
              [classes.evidenceBank]: student.work_state === 'evidence_bank',
              [classes.published]: student.work_state === 'published'
            })}>
            <FontAwesomeIcon
              icon={
                !student.work_state || student.work_state === 'pa_draft'
                  ? faClipboard
                  : faClipboardCheck
              }
            />
          </div>
        </div>
      </Component>
    )
  }
)

const filterStyles = ({ COLORS }) => ({
  container: {
    background: '#fff',
    '@media (min-width: 813px)': {
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }
  },
  sessionsSelectSection: {
    padding: '20px',
    borderBottom: '1px solid #ececec',
    fontSize: '14px',
    '& h2': {
      fontSize: '1.51em',
      lineHeight: '1.2',
      display: 'none',
      margin: '.5em 0 .6em',
      '@media (min-width: 813px)': {
        display: 'block'
      }
    }
  },
  sessionSelectSkeleton: {
    height: '40px',
    borderRadius: '4px'
  },
  studentsList: {
    '@media (min-width: 813px)': {
      flex: '1',
      overflow: 'auto'
    }
  },
  student: {
    width: '100%',
    padding: '12px 20px',
    borderBottom: '1px solid #ececec',
    display: 'flex',
    alignItems: 'center'
  },
  skeletonAvatar: {
    flexShrink: '0',
    width: '32px',
    height: '32px',
    borderRadius: '100%',
    marginRight: '10px'
  },
  skeletonName: {
    width: '120px'
  },
  backButton: {
    margin: '20px 0 20px 20px'
  },
  addStudent: {
    padding: '20px',
    borderBottom: '1px solid #ececec',
    '@media (min-width: 575px)': {
      border: 'none'
    },
    '& button': {
      textDecoration: 'underline',
      color: COLORS.TEXT.LIGHT_GREY.NORMAL
    }
  }
})

const Filter = injectSheet(filterStyles)(({ classes }) => {
  const { filters } = useSelector(state => getFeedItemsState(state))
  const dispatch = useDispatch()
  const [sessionsList, setSessionsList] = useState(null)
  const lastUpdate = usePrevious(filters.updateAfter)
  const [pupilSelectorOpened, setPupilSelectorOpened] = useState(false)
  const sessionsListFormatted = useMemo(
    () =>
      sessionsList &&
      sessionsList.map(session => ({
        ...session,
        dateFormat: format(new Date(session.accessible_at), 'MMM dd')
      })),
    [sessionsList]
  )

  const sessionListMapped = useMemo(
    () => {
      if (!sessionsListFormatted) return {}
      const result = {}
      sessionsListFormatted.forEach(session => (result[session.id] = session))
      return result
    },
    [sessionsListFormatted]
  )

  const selectedSession = useMemo(
    () => {
      return sessionListMapped[filters.session_id]
    },
    [sessionListMapped, filters.session_id]
  )

  const [studentsPerSession, setStudentsPerSession] = useState(null)

  const studentsPerSessionMap = useMemo(
    () => {
      if (!Array.isArray(studentsPerSession)) return {}
      const result = {}
      studentsPerSession.forEach(student => (result[student.id] = true))
      return result
    },
    [studentsPerSession]
  )

  const { selectedStudentState, setSelectedStudentState } = useContext(
    FeedContext
  )

  const selectedStudent = useMemo(
    () => {
      if (!selectedStudentState.studentId) return null
      return studentsPerSession.filter(
        student => student.id === selectedStudentState.studentId
      )[0]
    },
    [selectedStudentState, studentsPerSession]
  )

  // Fetch sessions
  useEffect(() => {
    apiGetMe({ include: 'account.pa_sessions,account.pa_session.details' })
      .catch(errorHandler)
      .then(res =>
        setSessionsList(res.data.account.pa_sessions.reverse().slice(0, 50))
      )
  }, [])

  // Fetch students per session and filter feed
  useEffect(
    () => {
      if (!filters.session_id) {
        setStudentsPerSession(null)
      } else {
        setStudentsPerSession('pending')
        apiGetStudentsPerSession(filters.session_id, {
          include: 'account.names,account.avatar'
        }).then(res => setStudentsPerSession(res.data.students_data))
      }
    },
    [filters.session_id]
  )

  // Update student stats when the work rejected or approved
  useEffect(
    () => {
      if (lastUpdate) {
        apiGetStudentsPerSession(filters.session_id, {
          include: 'account.names,account.avatar'
        }).then(res => setStudentsPerSession(res.data.students_data))
      }
    },
    [filters.updateAfter] // eslint-disable-line
  )

  return (
    <div className={classes.container}>
      <div className={classes.sessionsSelectSection}>
        <h2>Filter by...</h2>
        {!sessionsListFormatted && (
          <Bone className={classes.sessionSelectSkeleton} />
        )}
        {sessionsListFormatted && (
          <FormSelect
            disabled={!!selectedStudent}
            value={filters.session_id || 'all'}
            onChange={e => {
              dispatch(
                fetchFeedItems(
                  e.target.value !== 'all' && {
                    session_id: e.target.value
                  }
                )
              ).catch(errorHandler)
            }}>
            <option value="all">All sessions</option>
            {sessionsListFormatted.map(session => (
              <option key={session.id} value={session.id}>
                {session.title} - {session.dateFormat}
              </option>
            ))}
          </FormSelect>
        )}
      </div>
      <div className={classes.studentsList}>
        {selectedStudent && (
          <div>
            <Button
              action={() =>
                setSelectedStudentState({ studentId: null, sessionId: null })
              }
              className={classes.backButton}
              bleed
              back
              simple>
              Back
            </Button>
            <StudentItem active as="div" student={selectedStudent} />
          </div>
        )}
        {!selectedStudent && (
          <>
            {studentsPerSession &&
              studentsPerSession === 'pending' && (
                <div className={classes.studentsSkeleton}>
                  {[...Array(4).keys()].map(i => (
                    <div key={i} className={classes.student}>
                      <Bone className={classes.skeletonAvatar} />
                      <Bone className={classes.skeletonName} />
                    </div>
                  ))}
                </div>
              )}
            {studentsPerSession &&
              studentsPerSession !== 'pending' && (
                <>
                  {studentsPerSession.map(student => {
                    return (
                      <StudentItem
                        key={student.id}
                        student={student}
                        onClick={() =>
                          setSelectedStudentState({
                            studentId: student.id,
                            sessionId: filters.session_id
                          })
                        }
                      />
                    )
                  })}
                  {selectedSession &&
                    !selectedSession.student_solo_session && (
                      <div className={classes.addStudent}>
                        <ButtonElement
                          onClick={() => setPupilSelectorOpened(true)}>
                          Add students
                        </ButtonElement>
                      </div>
                    )}
                  <PupilSelector
                    onSelect={pupilIds => {
                      dispatch(
                        addStudentsToSession(filters.session_id, pupilIds)
                      ).catch(errorHandler)
                    }}
                    exclude={studentsPerSessionMap}
                    isOpen={pupilSelectorOpened}
                    requestClose={() => setPupilSelectorOpened(false)}
                  />
                </>
              )}
          </>
        )}
      </div>
    </div>
  )
})

const successModalStyles = {
  content: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    fontSize: '14px',
    lineHeight: '1.2',
    textAlign: 'center',
    minWidth: 'auto',
    maxWidth: 'calc(100vw - 40px)',
    width: '30em',
    padding: '2em',
    '& img': {
      margin: '0 0 2em',
      width: '12.5em',
      height: '12.5em'
    },
    '& h3': {
      fontSize: '1.57em',
      margin: '0 0 1em'
    },
    '& p': {
      margin: '0'
    },
    '& button': {
      margin: '2em 0 1em'
    }
  }
}

const SuccessModal = injectSheet(successModalStyles)(
  ({ classes, data, ...props }) => {
    return (
      <Modal closeButton {...props}>
        {data && (
          <ModalContent legacy className={classes.content}>
            <img src={duckRocket} alt="Duck in the rocket" />
            <h3>And away they go</h3>
            {data.workState === 'published' && (
              <p>
                The work is now published on Pobble.
                <br />
                Your pupil will be so excited!
              </p>
            )}
            {data.workState === 'evidence_bank' && (
              <p>
                The work is now saved on the
                <br />
                evidence bank.
              </p>
            )}
            <Button tall action={props.requestClose}>
              Back to my actions
            </Button>
          </ModalContent>
        )}
      </Modal>
    )
  }
)

const ApproveModal = ({ classes, data, ...props }) => {
  return !data ? null : (
    <Modal {...props}>
      <ApproveForm data={data} {...props} />
    </Modal>
  )
}

const ApproveForm = ({ data, showSuccessModal, requestClose }) => {
  const dispatch = useDispatch()
  const { approveWorkRequest } = useSelector(state => getFeedItemsState(state))
  const mapProps = useMemo(
    () => {
      if (!data) return {}
      const session = data.pa_action.pa_session
      const student = data.pa_action.student
      return {
        pupilId: student.id,
        pupilName: student.first_name,
        canPublish: student.student_type.policy.can_publish,
        defaultValues: {
          title: session.title || '',
          description: session.description || ''
        },
        onSubmit: fields => {
          const payload = {
            state: fields.workState,
            work_title: fields.title,
            work_description: fields.description,
            comment_on_work: fields.comment,
            work_tags: fields.tags
          }
          if (fields.sta) payload.independent = fields.sta.value
          if (fields.sta && fields.sta.extraInfo)
            payload.independent_description = fields.sta.extraInfo
          return dispatch(
            approveWork({
              workVersionId: data.pa_action.last_pa_work_version.id,
              feedId: data.id,
              data: payload
            })
          )
            .then(() => showSuccessModal({ workState: fields.workState }))
            .catch(errorHandler)
        }
      }
    },
    [data, dispatch, showSuccessModal]
  )
  return (
    <WorkForm
      {...mapProps}
      onCancel={requestClose}
      submitButton={
        <Button
          pending={approveWorkRequest.pending}
          type="submit"
          tall
          icon={<FontAwesomeIcon icon={faCheck} />}>
          Approve writing
        </Button>
      }
    />
  )
}

const SendBackForm = ({ data, requestClose }) => {
  const feedId = data.id
  const workVersionId = data.pa_action.last_pa_work_version.id
  const pupilName = data.pa_action.student.first_name
  const dispatch = useDispatch()
  const [comment, setComment] = useState('')
  const { rejectWorkRequest } = useSelector(state => getFeedItemsState(state))
  return (
    <form
      onSubmit={e => {
        e.preventDefault()
        dispatch(rejectWork({ feedId, workVersionId, comment }))
          .catch(errorHandler)
          .then(() => {
            toast.success(`Work has been sent back to ${pupilName}`)
            requestClose()
          })
      }}>
      <ModalContent legacy>
        <section>
          <FormLabel small>
            <strong>Add a comment</strong>
            &nbsp;(only {pupilName} will see this)
          </FormLabel>
          <FormTextArea
            onChange={e => setComment(e.currentTarget.value)}
            placeholder="e.g. This photo is blurry"
          />
        </section>
      </ModalContent>
      <ModalFooter alignRight childrenGap>
        <Button tall simple action={requestClose}>
          Cancel
        </Button>
        <Button
          pending={rejectWorkRequest.pending}
          icon={<FontAwesomeIcon icon={faPaperPlane} />}
          tall
          type="submit">
          Send back
        </Button>
      </ModalFooter>
    </form>
  )
}

const SendBackModal = ({ classes, data, ...props }) => {
  return (
    <Modal closeButton {...props}>
      <ModalHeader>Send back</ModalHeader>
      <SendBackForm data={data} requestClose={props.requestClose} />
    </Modal>
  )
}

const mediaPageNavStyles = ({ COLORS }) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '20px',
    color: '#fff',
    '& button': {
      cursor: 'pointer',
      color: '#fff',
      padding: '0',
      border: 'none',
      background: 'none',
      fontSize: '1.6em',
      '&:active': {
        color: COLORS.GREEN.NORMAL
      }
    },
    '& span': {
      fontSize: '0.8em',
      width: '3em',
      textAlign: 'center'
    }
  }
})

const MediaPageNav = injectSheet(mediaPageNavStyles)(
  ({ classes, currentPage, className, pagesLength, setCurrentPage }) => {
    return (
      <div className={classNames(classes.container, className)}>
        <button
          onClick={() =>
            setCurrentPage(
              currentPage === 0 ? pagesLength - 1 : currentPage - 1
            )
          }>
          <FontAwesomeIcon icon={faCaretCircleLeft} />
        </button>
        <span>
          {currentPage + 1} / {pagesLength}
        </span>
        <button
          onClick={() =>
            setCurrentPage(
              currentPage === pagesLength - 1 ? 0 : currentPage + 1
            )
          }>
          <FontAwesomeIcon icon={faCaretCircleRight} />
        </button>
      </div>
    )
  }
)

const feedContentHolderStyles = {
  container: {
    padding: '0 16px'
  }
}

const FeedContentHolder = injectSheet(feedContentHolderStyles)(
  ({ classes, className, ...props }) => {
    return (
      <div className={classNames(classes.container, className)} {...props} />
    )
  }
)

const successCriteriaStyles = ({ FONT_SIZES }) => ({
  successCriteria: {
    border: '1px solid #4a4a4a',
    borderRadius: '8px',
    overflow: 'hidden',
    background: '#fff',
    '& ul': {
      listStyle: 'none',
      padding: '0',
      margin: '0'
    },
    '& li': {
      display: 'flex',
      alignItems: 'center',
      padding: '16px 20px',
      borderTop: '1px solid #4a4a4a',
      '&:first-child': {
        borderTop: '0'
      }
    },
    '& p': {
      margin: '0'
    }
  },
  successCriteriaHead: {
    background: '#4a4a4a',
    padding: '16px 20px',
    color: '#fff',
    display: 'block',
    fontWeight: 'bold',
    ...FONT_SIZES.BODY_DEFAULT.STYLE
  },
  criteria: {
    flex: '1'
  },
  criterion: {
    fontWeight: 'bold'
  }
})

const SuccessCriteria = injectSheet(successCriteriaStyles)(
  ({ classes, data, onChange }) => {
    return (
      <div className={classes.successCriteria}>
        <span className={classes.successCriteriaHead}>Have I...</span>
        <ul>
          {data.map(item => {
            return (
              <li>
                <div className={classes.criteria}>
                  <p className={classes.criterion}>{item.criterion}</p>
                  <p>{item.prompts}</p>
                </div>
                <div className={classes.checkbox}>
                  <FormCheckbox disabled checked={item.checked} />
                </div>
              </li>
            )
          })}
        </ul>
      </div>
    )
  }
)

const boxStyles = ({ FONT_SIZES }) => ({
  box: {
    background: '#f8f8f8',
    padding: '20px',
    overflow: 'hidden',
    borderBottom: '1px solid #ececec',
    '&:last-child': {
      border: 'none'
    },
    '& > h2': {
      margin: '-20px -20px 20px',
      padding: '20px',
      background: '#fff',
      ...FONT_SIZES.BODY_DEFAULT.STYLE,
      fontWeight: 'bold'
    }
  }
})

const Box = injectSheet(boxStyles)(({ title, classes, children }) => {
  return (
    <div className={classes.box}>
      <h2>{title}</h2>
      {children}
    </div>
  )
})

const feedItemStyles = ({ COLORS }) => ({
  container: {
    borderRadius: '8px',
    background: '#fff',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.2)',
    overflow: 'hidden',
    fontSize: '14px',
    marginBottom: '2em',
    maxWidth: '1024px',
    margin: '0 auto',
    '@media (min-width: 575px)': {
      marginBottom: '3em'
    },
    '@media (min-width: 813px)': {
      width: '100%'
    }
  },
  head: {
    display: 'flex',
    padding: '1.4em 0',
    '& h3': {
      fontSize: '1.57em',
      lineHeight: '1.2',
      margin: '0'
    }
  },
  layout: {
    '@media (min-width: 575px)': {
      display: 'flex'
    }
  },
  layoutDetails: {
    background: '#f8f8f8',
    '@media (min-width: 575px)': {
      borderTop: '1px solid #eee',
      flex: '1',
      maxWidth: '360px',
      position: 'relative',
      '& > div': {
        position: 'absolute',
        left: '0',
        top: '0',
        height: '100%',
        width: '100%',
        overflow: 'auto'
      }
    }
  },
  layoutWork: {
    flex: '1'
  },
  media: {
    position: 'relative'
  },
  work: {
    background: '#333',
    padding: '1em',
    flexDirection: 'column-reverse',
    display: 'flex',
    '@media (min-width: 575px)': {
      padding: '20px',
      display: 'flex'
    }
  },
  controls: {
    display: 'flex',
    margin: '0 0 14px',
    justifyContent: 'space-between',
    '@media (min-width: 575px)': {
      margin: '0 0 20px'
    }
  },
  footer: {
    paddingTop: '1.2em',
    paddingBottom: '1.2em',
    display: 'flex',
    justifyContent: 'flex-end',
    '& > *': {
      marginLeft: '1em'
    },
    '@media (min-width: 575px)': {
      position: 'relative',
      zIndex: '50',
      boxShadow: '0 -3px 3px rgba(0, 0, 0, 0.1)'
    }
  },
  author: {
    background: '#fff',
    padding: '1em 0',
    borderBottom: '1px solid #ececec',
    '& > *': {
      display: 'flex',
      alignItems: 'center'
    }
  },
  avatar: {
    fontSize: '2.7em',
    marginRight: '.2em'
  },
  pupilName: {
    fontWeight: 'bold',
    margin: '0',
    lineHeight: '1.2'
  },
  creator: {
    margin: '0',
    fontSize: '.86em',
    lineHeight: '1.2',
    color: COLORS.TEXT.LIGHT_GREY.NORMAL
  },
  versionSelect: {
    '& select': {
      border: 'none',
      background: '#000',
      color: '#fff'
    },
    '& svg': {
      color: '#fff !important'
    }
  }
})

const FeedItem = injectSheet(feedItemStyles)(
  ({ classes, data, onSendBack, onApprove }) => {
    const [currentMediaPage, setCurrentMediaPage] = useState(0)
    const [currentVersionIndex, setCurrentVersionIndex] = useState(
      data.pa_work_versions.length - 1
    )

    const md = useMemo(
      () => {
        const { pa_work_versions } = data
        const currentWorkVersion = pa_work_versions[currentVersionIndex]
        const { student } = data
        return {
          title: data.pa_session.title,
          media: !currentWorkVersion.work ? [] : currentWorkVersion.work.media,
          pupilName: student.full_name,
          pupilAvatar: student.avatar,
          successCriteria: currentWorkVersion.success_criteria,
          action: data,
          versions: pa_work_versions
        }
      },
      [data, currentVersionIndex]
    )

    const workReviewsProps = useWorkReviews(md.action)

    const activeMedia = md.media[currentMediaPage]

    return (
      <div className={classes.container}>
        <div className={classes.head}>
          <FeedContentHolder>
            <h3>{md.title}</h3>
          </FeedContentHolder>
        </div>
        <div className={classes.layout}>
          <div className={classes.layoutWork}>
            <div className={classes.work}>
              {activeMedia && (
                <div className={classes.media}>
                  {activeMedia.pa_highlight_canva && (
                    <WorkHighlighter
                      key={activeMedia.pa_highlight_canva.data}
                      preloadData={activeMedia.pa_highlight_canva.data}
                    />
                  )}
                  <MediaLoader key={currentMediaPage} media={activeMedia} />
                </div>
              )}
              <div className={classes.controls}>
                <FormSelect
                  className={classes.versionSelect}
                  value={currentVersionIndex}
                  onChange={e => setCurrentVersionIndex(e.target.value)}>
                  {md.versions.map((version, index) => {
                    return <option value={index}>Version {index + 1}</option>
                  })}
                </FormSelect>
                <MediaPageNav
                  setCurrentPage={setCurrentMediaPage}
                  pagesLength={md.media.length}
                  currentPage={currentMediaPage}
                />
              </div>
            </div>
          </div>
          <div className={classes.layoutDetails}>
            <div>
              <div className={classes.author}>
                <FeedContentHolder>
                  <PobbleAvatar
                    className={classes.avatar}
                    avatar={md.pupilAvatar}
                  />
                  <div>
                    <p className={classes.pupilName}>{md.pupilName}</p>
                    <p className={classes.creator}>Creator</p>
                  </div>
                </FeedContentHolder>
              </div>
              {!!md.successCriteria.length && (
                <Box title="Success criteria">
                  <SuccessCriteria data={md.successCriteria} />
                </Box>
              )}
              {!!workReviewsProps.reviews && (
                <Box title="Comments">
                  <WorkReviews {...workReviewsProps} />
                </Box>
              )}
            </div>
          </div>
        </div>
        <FeedContentHolder className={classes.footer}>
          <Button
            action={onSendBack}
            tall
            secondary
            icon={<FontAwesomeIcon icon={faPaperPlane} />}>
            Send back
          </Button>
          <Button
            action={onApprove}
            tall
            icon={<FontAwesomeIcon icon={faCheck} />}>
            Approve writing
          </Button>
        </FeedContentHolder>
      </div>
    )
  }
)

const selectedStudentScreenStyles = ({ COLORS }) => ({
  pending: {
    flex: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  teacherReviewContainer: {
    padding: '20px',
    '@media (min-width: 575px)': {
      padding: '30px 20px'
    },
    '@media (min-width: 813px) and (orientation: landscape)': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      flex: '1',
      overflow: 'auto',
      '& *': {
        flexShrink: '0'
      }
    }
  },
  studentInteractionContainer: {
    flex: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  studentInteractionHolder: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '0 40px',
    '& > p': {
      margin: '15px 0'
    }
  },
  statusIcon: {
    background: COLORS.BLUE.NORMAL,
    width: '80px',
    height: '80px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '30px',
    color: '#fff',
    borderRadius: '100%'
  }
})

const SelectedStudentScreen = injectSheet(selectedStudentScreenStyles)(
  ({ classes }) => {
    const [paAction, setPaAction] = useState(null)

    const { selectedStudentState } = useContext(FeedContext)

    useEffect(
      () => {
        if (!selectedStudentState.studentId) return false
        apiGetPresentationAction({
          pa_session_id: selectedStudentState.sessionId,
          student_account_id: selectedStudentState.studentId,
          include:
            'pa_action.status,pa_action.presentation.names,pa_action.presentation.image,pa_session.timestamps,pa_action.last_pa_work_version.work.media,pa_action.last_pa_work_version.work.status,pa_action.last_pa_work_version.work.medium.image,pa_action.last_pa_work_version.work.medium.details,pa_action.student.names,pa_action.student.avatar,pa_action.student.student_type.policy,pa_action.pa_session.details'
        })
          .then(res => setPaAction(res.data.pa_presentation_action))
          .catch(errorHandler)
      },
      [selectedStudentState]
    )
    return !paAction ? (
      <div className={classes.pending}>
        <Spinner />
      </div>
    ) : paAction.status === 'teacher_review' ? (
      <div className={classes.teacherReviewContainer}>
        <FeedItem data={paAction} />
      </div>
    ) : (
      <div className={classes.studentInteractionContainer}>
        <div className={classes.studentInteractionHolder}>
          <div className={classes.statusIcon}>
            <FontAwesomeIcon
              icon={paAction.status === 'done' ? faClipboardCheck : faFile}
            />
          </div>
          {paAction.status === 'done' && (
            <p>
              {paAction.student.first_name} already got an approved work.
              <br />
              You can access{' '}
              <PobbleLink
                href={`${URLS.DASHBOARD}/${paAction.student.id}/work`}>
                his evidence bank
              </PobbleLink>
              .
            </p>
          )}
          {paAction.status !== 'done' && (
            <p>
              {paAction.student.first_name} hasn’t submitted a piece of work yet
            </p>
          )}
        </div>
      </div>
    )
  }
)

const FeedContext = createContext(null)

const feedStyles = {
  container: {
    ...fullVHExceptHeader,
    display: 'flex',
    flexDirection: 'column',
    '& > *': {
      flexShrink: '0'
    }
  },
  filterButton: {
    marginRight: 'auto',
    '@media (min-width: 813px) and (orientation: landscape)': {
      display: 'none'
    }
  },
  filter: {
    display: 'none',
    '@media (min-width: 813px) and (orientation: landscape)': {
      width: '250px',
      display: 'block',
      background: '#fff'
    },
    '@media (min-width: 1025px)': {
      width: '386px'
    }
  },
  showFilters: {
    '& $filter': {
      display: 'block'
    }
  },
  pending: {
    flex: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  head: {
    justifyContent: 'flex-end'
  },
  layout: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1',
    '@media (min-width: 813px)  and (orientation: landscape)': {
      flexDirection: 'row',
      overflow: 'hidden'
    }
  },
  items: {
    padding: '20px',
    '@media (min-width: 575px)': {
      padding: '30px 20px'
    },
    '@media (min-width: 813px) and (orientation: landscape)': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      flex: '1',
      overflow: 'auto',
      '& > *': {
        flexShrink: '0'
      }
    }
  },
  noItems: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    padding: '2em',
    '& h3': {
      fontSize: '1.57em',
      lineHeight: '1.2',
      margin: '0 0 1em'
    }
  }
}

const Feed = injectSheet(feedStyles)(({ classes }) => {
  const history = useHistory()
  const match = useRouteMatch()
  const dispatch = useDispatch()
  const feedItems = useSelector(state => getFeedItemsList(state))

  const [modalState, setModalState] = useState({ type: null, data: null })
  const [showFilters, setShowFilters] = useState(false)
  const { fetchItemsRequest } = useSelector(state => getFeedItemsState(state))
  const [selectedStudentState, setSelectedStudentState] = useState({
    studentId: null,
    sessionId: null
  })

  const { uploadWork, uploadingWork } = useWorkUpload()

  useEffect(
    () => {
      setModalState(
        match.path === '/upload'
          ? { type: 'DirectUpload', data: null }
          : { type: null, data: null }
      )
    },
    [match.path]
  )

  useEffect(
    () => {
      dispatch(fetchFeedItems())
    },
    [dispatch]
  )

  const contextValue = useMemo(
    () => {
      return {
        selectedStudentState,
        setSelectedStudentState
      }
    },
    [selectedStudentState, setSelectedStudentState]
  )

  return (
    <FeedContext.Provider value={contextValue}>
      <div
        className={classNames(classes.container, {
          [classes.showFilters]: showFilters
        })}>
        <PageHead className={classes.head}>
          <Button
            action={() => setShowFilters(!showFilters)}
            className={classes.filterButton}
            simple
            icon={<FontAwesomeIcon icon={faFilter} />}>
            Filter
          </Button>
          <Button
            simple={uploadingWork}
            pending={uploadingWork}
            action={() => {
              return history.push('/upload')
            }}
            tall
            icon={<FontAwesomeIcon icon={faPlus} />}>
            {uploadingWork && 'Uploading work...'}
            {!uploadingWork && 'Add Work'}
          </Button>
        </PageHead>
        <div className={classes.layout}>
          <div className={classes.filter}>
            <Filter />
          </div>
          {selectedStudentState.studentId ? (
            <SelectedStudentScreen />
          ) : (
            <>
              {(!feedItems || fetchItemsRequest.pending) && (
                <div className={classes.pending}>
                  <Spinner />
                </div>
              )}
              {feedItems &&
                !fetchItemsRequest.pending && (
                  <>
                    {!feedItems.length && (
                      <div className={classes.noItems}>
                        <h3>Nothing to review</h3>
                        <img src={mountains} alt="Mountains" />
                      </div>
                    )}
                    {!!feedItems.length && (
                      <div className={classes.items}>
                        {feedItems.map(feedItem => (
                          <FeedItem
                            onSendBack={() => {
                              openConfirmationDialog({
                                bodyText: `Are you sure you want to send this work back to the student?`,
                                confirmText: 'Send back',
                                cancelText: 'Cancel',
                                action: () => {
                                  toast.success(
                                    `Work has been sent back to ${
                                      feedItem.pa_action.student.first_name
                                    }`
                                  )
                                  dispatch(
                                    rejectWork({
                                      feedId: feedItem.id,
                                      workVersionId:
                                        feedItem.pa_action.last_pa_work_version
                                          .id
                                    })
                                  ).catch(errorHandler)
                                }
                              })
                            }}
                            onApprove={() =>
                              setModalState({
                                type: 'Approve',
                                data: feedItem
                              })
                            }
                            key={feedItem.id}
                            data={feedItem.pa_action}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
            </>
          )}
        </div>
        <ApproveModal
          showSuccessModal={data =>
            setModalState({
              type: 'Success',
              data: { ...modalState.data, ...data }
            })
          }
          requestClose={() =>
            setModalState({ type: null, data: modalState.data })
          }
          isOpen={modalState.type === 'Approve'}
          data={modalState.data}
        />
        <SendBackModal
          requestClose={() =>
            setModalState({ type: null, data: modalState.data })
          }
          isOpen={modalState.type === 'SendBack'}
          data={modalState.data}
        />
        <SuccessModal
          requestClose={() =>
            setModalState({ type: null, data: modalState.data })
          }
          data={modalState.data}
          isOpen={modalState.type === 'Success'}
        />
        <DirectUploadModal
          onUpload={({ files, pupilId }) => {
            history.push('/')
            uploadWork(pupilId, files)
          }}
          requestClose={() => history.push('/')}
          isOpen={modalState.type === 'DirectUpload'}
        />
      </div>
    </FeedContext.Provider>
  )
})

export default Feed
